import { escapeHtml } from '../util/escapeHtml.js';
import { api, HttpError } from '../api/client.js';

/**
 * Returns true if the HttpError is the server's email-verification gate.
 * The middleware returns 403 with code EMAIL_NOT_VERIFIED.
 */
export function isEmailNotVerifiedError(err: unknown): err is HttpError {
  return err instanceof HttpError && err.code === 'EMAIL_NOT_VERIFIED';
}

/**
 * Drop-in inline alert HTML for a "verify your email first" prompt.
 * Returns a string of HTML that callers can stick inside a flash slot.
 *
 * Use together with `bindVerifyPromptButtons(root)` after rendering to
 * activate the embedded Resend button.
 */
export function verifyEmailPromptHtml(message?: string): string {
  const text = message
    ?? 'Confirm thy email to perform this action. Check thy inbox for the verification scroll, or request a new one below.';
  return `
    <div class="alert danger verify-prompt" style="display:flex; flex-direction:column; gap: var(--s-2);">
      <div>📜 ${escapeHtml(text)}</div>
      <div style="display:flex; gap: var(--s-2); flex-wrap: wrap;">
        <button class="btn" data-verify-resend>Resend verification scroll</button>
        <a class="btn ghost" href="#/settings">Go to Settings</a>
      </div>
    </div>
  `;
}

/**
 * Wire the [data-verify-resend] button inside `root` to actually call
 * the resend endpoint. Safe to call multiple times — only the first
 * non-disabled button per render binds.
 */
export function bindVerifyPromptButtons(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>('button[data-verify-resend]').forEach((btn) => {
    if (btn.dataset.verifyBound === '1') return;
    btn.dataset.verifyBound = '1';
    btn.addEventListener('click', async () => {
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Sending…';
      try {
        await api.resendVerification();
        btn.textContent = 'Sent ✓ — check thy inbox';
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 4000);
      } catch {
        btn.textContent = 'Failed — try again';
        btn.disabled = false;
      }
    });
  });
}

